import { Button, ExpandableSection, SpaceBetween, Tabs } from '@amzn/awsui-components-react';
import TPTaxReportTableGrid from 'src/components/reports/TPTaxReportTableGrid';
import React from 'react';
import { ReportMetaData, TPTaxReportMapResponse } from 'src/models/reports/TPTaxReport';
import ServiceCollection from 'src/services/ServiceCollection';
import CONSTANTS, { ALLOCATION, DETAIL, TP_PNL, IS_TESTED_PARTY_REPORT_ENABLED } from 'src/utils/constants';
import { useHistory } from 'react-router-dom';
import { MANAGE_EXCEPTIONS_TAB_URL_PREFIX } from 'src/components/ic-account-mapping/constants';
import {
    MultiCLIPassthroughCostSection,
    MultiCLIPLIReconciliationSection,
} from 'src/components/reports/PassthroughCostSections';
import { buildCliHeaderLabels, formatCliWithCurrency, buildCliCurrencyMap } from 'src/components/reports/TPMultiCLIReportsShared';

/** Filters a CLI-keyed map to only include CLIs present in the current page's reportHeaders. */
function filterCliMap<T>(cliMap: { [cli: string]: T } | null | undefined, reportHeaders: string[] | undefined): { [cli: string]: T } | null | undefined {
    if (!cliMap || !reportHeaders || reportHeaders.length === 0) return cliMap;
    const visibleCLIs = new Set(reportHeaders);
    const filtered: { [cli: string]: T } = {};
    for (const cli of Object.keys(cliMap)) {
        if (visibleCLIs.has(cli)) {
            filtered[cli] = cliMap[cli];
        }
    }
    return Object.keys(filtered).length > 0 ? filtered : null;
}

export default function TPMultiCLIReportTableGrid(props: {
    reportData: TPTaxReportMapResponse;
    services: ServiceCollection;
    fetchReportLoading: boolean;
    reportMetaData?: ReportMetaData;
    isPassthroughDrilldownEnabled?: boolean;
    // Gated behind the reportCurrencyOverlay AppConfig flag. When false, CLI headers/tab labels
    // and the per-CLI currency line render without FRAMES currency (pre-feature behaviour).
    showCliCurrency?: boolean;
}) {
    const history = useHistory();

    // CLI column headers/tab labels in the LEF & CWB (multi-CLI) views are annotated with the
    // FRAMES currency sourced from the DETAIL section, e.g. "<cli>" -> "<cli> (USD)".
    // Only when the currency-display feature flag is on; otherwise everything falls back to raw CLIs.
    const showCliCurrency = props.showCliCurrency ?? false;
    const cliHeaderLabels = showCliCurrency
        ? buildCliHeaderLabels(props.reportData, props.reportData?.reportHeaders)
        : undefined;
    const cliCurrencyMap = showCliCurrency ? buildCliCurrencyMap(props.reportData) : {};

    return (
        <SpaceBetween size={'xl'}>
            <ExpandableSection variant="container" headerText="Details" disableContentPaddings={true}>
                <TPTaxReportTableGrid
                    columnHeaders={props.reportData?.reportHeaders}
                    columnHeaderLabels={cliHeaderLabels}
                    data={props.reportData?.reportContent?.DETAIL}
                    services={props.services}
                    loadingStatus={props.fetchReportLoading}
                />
            </ExpandableSection>
            <ExpandableSection
                variant="container"
                headerText={IS_TESTED_PARTY_REPORT_ENABLED ? 'Tested Party TP P&L' : 'TP Profit and Loss (P&L)'}
                disableContentPaddings={true}
                headerActions={
                    props.reportMetaData?.reportName === CONSTANTS.REPORT_TYPES.TP_TAX_CWB_REPORT &&
                    !!props.reportMetaData?.workbookLabel ? (
                        <Button
                            onClick={() => {
                                history.push(
                                    `${MANAGE_EXCEPTIONS_TAB_URL_PREFIX}?workbookLabel=${encodeURIComponent(props.reportMetaData?.workbookLabel as string)}`
                                );
                            }}>
                            {' '}
                            Manage P&L Mapping{' '}
                        </Button>
                    ) : null
                }>
                <TPTaxReportTableGrid
                    columnHeaders={props.reportData?.reportHeaders}
                    columnHeaderLabels={cliHeaderLabels}
                    data={props.reportData?.reportContent?.TP_PNL}
                    services={props.services}
                    loadingStatus={props.fetchReportLoading}
                />
            </ExpandableSection>
            <ExpandableSection
                variant="container"
                headerText={IS_TESTED_PARTY_REPORT_ENABLED ? 'Tested Party PLI' : 'Profit Level Indicator (PLI)'}
                disableContentPaddings={true}
                defaultExpanded={true}>
                <Tabs
                    tabs={Object.entries(props.reportData?.reportContent || {})
                        .filter(([key]) => key !== DETAIL && key !== TP_PNL && key !== ALLOCATION)
                        .map(([key, value]) => ({
                            label: formatCliWithCurrency(key, cliCurrencyMap),
                            id: key,
                            content: (
                                <TPTaxReportTableGrid
                                    data={value}
                                    services={props.services}
                                    columnHeaders={props.reportData?.reportHeaders}
                                    columnHeaderLabels={cliHeaderLabels}
                                    loadingStatus={props.fetchReportLoading}
                                />
                            ),
                        }))}
                />
            </ExpandableSection>
            {props.isPassthroughDrilldownEnabled && (
                <>
                    <MultiCLIPassthroughCostSection
                        cliPassthroughDetails={filterCliMap(props.reportData?.cliPassthroughDetails, props.reportData?.reportHeaders)}
                        serviceCollection={props.services}
                        cliCurrencyMap={cliCurrencyMap}
                    />
                    <MultiCLIPLIReconciliationSection
                        cliPliReconciliation={filterCliMap(props.reportData?.cliPliReconciliation, props.reportData?.reportHeaders)}
                        serviceCollection={props.services}
                        cliCurrencyMap={cliCurrencyMap}
                    />
                </>
            )}
        </SpaceBetween>
    );
}
